import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, map, take } from 'rxjs';
import {
  ArtifactOverrideRequest,
  ArtifactResponse,
  ArtifactVM,
  BundleOverrideRequest,
  SoftwareSelectionResolutionDto,
  SoftwareVersionOverridesDto,
  SoftwareVersionOverridesRequest,
} from 'src/app/models/device.model';
import { MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';
import {
  LicenseBatchDto,
  LicensedComponentDto,
  ReleaseBundleDto,
} from 'src/app/models/license.model';
import { DeviceService } from 'src/app/services/device.service';
import { LicenseBatchService } from 'src/app/services/license-batch.service';
import { toLicenseBatchRequest } from 'src/app/utils/license-batch-request';
import {
  ReleaseOverrideOption,
  SoftwareOverrideComponent,
} from './device-software-override.types';

export interface SoftwareOverridesSaved {
  artifactOverrides: ArtifactResponse[];
  bundleOverrides: ReleaseBundleDto[];
  unresolvedOverrideIds: string[];
  softwareSelectionResolutions: SoftwareSelectionResolutionDto[];
}

interface PersistedOverrides {
  overrides?: SoftwareVersionOverridesDto;
  softwareSelectionResolutions?: SoftwareSelectionResolutionDto[];
}

@Component({
  selector: 'app-device-software-override',
  templateUrl: './device-software-override.component.html',
  styleUrls: ['./device-software-override.component.scss'],
  standalone: false,
})
export class DeviceSoftwareOverrideComponent implements OnInit {
  private readonly licenseBatchService = inject(LicenseBatchService);
  private readonly deviceService = inject(DeviceService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly modifiedReleaseIds = new Set<string>();
  private readonly removedBundleIds = new Set<string>();

  @Input({ required: true }) linkedLicenseBatches: LicenseBatchDto[] = [];
  /** The overrides belong to either this device or this license batch. */
  @Input() deviceId = '';
  @Input() licenseBatch: LicenseBatchDto | null = null;
  @Input() initialArtifactOverrides: ArtifactResponse[] = [];
  @Input() initialBundleOverrides: ReleaseBundleDto[] = [];
  /** Overrides whose artifact or bundle is gone from Spadok. */
  @Input() unresolvedOverrideIds: string[] = [];
  @Output() overridesSaved = new EventEmitter<SoftwareOverridesSaved>();

  softwareComponents: SoftwareOverrideComponent[] = [];
  releaseOptions: ReleaseOverrideOption[] = [];
  releasesLoading = false;
  releasesLoadError = '';
  supportedProjects: LicensedComponentDto[] = [];
  supportedProjectIds: string[] = [];
  saving = false;
  saveError = '';
  dryRunLoading = false;
  dryRunError = '';
  bundleDryRunCompleted = false;
  artifactDryRunCompleted = false;
  softwareSelectionResolutions: SoftwareSelectionResolutionDto[] = [];

  get hasIncompleteBundleSelection(): boolean {
    return this.releaseOptions.some(
      (option) => option.selected && !option.selectedBundle,
    );
  }

  get hasOverrideChanges(): boolean {
    if (this.hasIncompleteBundleSelection) {
      return false;
    }

    return (
      this.artifactDryRunCompleted &&
      (this.hasArtifactOverrideChanges || this.hasBundleOverrideChanges)
    );
  }

  get bundleOverridesForReview(): ReleaseBundleDto[] {
    return this.getBundleOverridesForSave();
  }

  /** Not tied to any release that is still listed, so they are removed on their own. */
  get unresolvedBundles(): ReleaseBundleDto[] {
    const unresolvedIds = new Set(this.unresolvedOverrideIds);
    return this.initialBundleOverrides.filter(
      (bundle) =>
        unresolvedIds.has(bundle.id) && !this.removedBundleIds.has(bundle.id),
    );
  }

  get forLicenseBatch(): boolean {
    return !!this.licenseBatch;
  }

  private get targetId(): string {
    return (
      this.licenseBatch ? (this.licenseBatch.id ?? '') : this.deviceId
    ).trim();
  }

  private get hasArtifactOverrideChanges(): boolean {
    const initialOverrides = this.initialArtifactOverrides
      .map((artifact) => `${artifact.projectId}:${artifact.id}`)
      .sort();
    const selectedOverrides = this.softwareComponents
      .map((component) => component.selectedArtifact)
      .filter((artifact): artifact is ArtifactVM => !!artifact)
      .map((artifact) => `${artifact.projectId}:${artifact.id}`)
      .sort();

    return initialOverrides.join('|') !== selectedOverrides.join('|');
  }

  private get hasBundleOverrideChanges(): boolean {
    const initialBundleIds = this.initialBundleOverrides
      .map((bundle) => bundle.id)
      .sort();
    const selectedBundleIds = this.getBundleOverridesForSave()
      .map((bundle) => bundle.id)
      .sort();

    return initialBundleIds.join('|') !== selectedBundleIds.join('|');
  }

  ngOnInit(): void {
    this.initializeArtifactOverrides();
    this.loadReleases();
  }

  getProductArtifacts(component: SoftwareOverrideComponent): void {
    if (component.artifactsLoading || component.artifactsLoaded) {
      return;
    }

    component.artifactsLoading = true;
    component.artifactsLoadError = undefined;
    this.licenseBatchService
      .getProductArtifacts(component.id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (artifacts) => {
          const initialOverride = this.initialArtifactOverrides.find(
            (artifact) => artifact.projectId === component.id,
          );
          const selectedArtifact = component.selectionChanged
            ? component.selectedArtifact
            : initialOverride
              ? (artifacts.find(
                  (artifact) => artifact.id === initialOverride.id,
                ) ?? this.toArtifactVM(initialOverride))
              : undefined;

          component.artifacts =
            selectedArtifact &&
            !artifacts.some((artifact) => artifact.id === selectedArtifact.id)
              ? [selectedArtifact, ...artifacts]
              : artifacts;
          component.selectedArtifact = selectedArtifact;
          component.artifactsLoaded = true;
          component.artifactsLoading = false;
        },
        error: (error: unknown) => {
          component.artifactsLoading = false;
          component.artifactsLoadError = this.getErrorMessage(
            error,
            this.translate.instant('software_override.errors.artifacts_load'),
          );
        },
      });
  }

  beginArtifactOverride(component: SoftwareOverrideComponent): void {
    component.artifactOverrideEditorOpen = true;
    this.getProductArtifacts(component);
  }

  cancelArtifactOverride(component: SoftwareOverrideComponent): void {
    component.artifactOverrideEditorOpen = false;
  }

  onArtifactSelected(
    component: SoftwareOverrideComponent,
    selectedArtifact: ArtifactVM | string,
  ): void {
    if (typeof selectedArtifact !== 'string') {
      component.selectedArtifact = selectedArtifact;
      component.artifactOverrideEditorOpen = false;
      component.selectionChanged = true;
      this.invalidateArtifactDryRun();
      this.saveError = '';
    }
  }

  clearArtifactSelection(component: SoftwareOverrideComponent): void {
    component.selectedArtifact = undefined;
    component.artifactOverrideEditorOpen = false;
    component.selectionChanged = true;
    this.invalidateArtifactDryRun();
    this.saveError = '';
  }

  toggleRelease(option: ReleaseOverrideOption): void {
    option.selected = !option.selected;
    this.modifiedReleaseIds.add(option.release.id);
    this.invalidateBundleDryRun();
    this.saveError = '';

    if (!option.selected) {
      option.selectedBundle = undefined;
    }
  }

  removeUnresolvedBundle(bundle: ReleaseBundleDto): void {
    this.removedBundleIds.add(bundle.id);
    this.invalidateBundleDryRun();
    this.saveError = '';
  }

  loadBundles(option: ReleaseOverrideOption): void {
    if (!option.selected || option.bundlesLoading || option.bundlesLoaded) {
      return;
    }

    option.bundlesLoading = true;
    option.bundlesLoadError = undefined;
    this.licenseBatchService
      .getBundles(option.release.id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bundles) => {
          const supportedProjectIds = new Set(this.supportedProjectIds);
          option.bundles = bundles;
          option.compatibleBundles = bundles.filter((bundle) =>
            this.containsSupportedProject(bundle, supportedProjectIds),
          );
          option.bundlesLoaded = true;
          option.bundlesLoading = false;

          const selectedBundleId = option.selectedBundle?.id;
          option.selectedBundle =
            bundles.find((bundle) => bundle.id === selectedBundleId) ??
            option.selectedBundle;
        },
        error: (error: unknown) => {
          option.bundlesLoading = false;
          option.bundlesLoadError = this.getErrorMessage(
            error,
            this.translate.instant('software_override.errors.bundles_load'),
          );
        },
      });
  }

  onBundleSelected(
    option: ReleaseOverrideOption,
    selectedBundle: ReleaseBundleDto | string,
  ): void {
    if (
      typeof selectedBundle !== 'string' &&
      option.compatibleBundles.some((bundle) => bundle.id === selectedBundle.id)
    ) {
      option.selectedBundle = selectedBundle;
      this.modifiedReleaseIds.add(option.release.id);
      this.invalidateBundleDryRun();
      this.saveError = '';
    }
  }

  continueToArtifacts(stepper: MatStepper): void {
    this.runDryRun('bundles', () => stepper.next());
  }

  continueToReview(stepper: MatStepper): void {
    this.runDryRun('artifacts', () => stepper.next());
  }

  saveOverrides(): void {
    const targetId = this.targetId;
    if (!targetId || !this.hasOverrideChanges || this.saving) {
      return;
    }

    const selectedArtifacts = this.softwareComponents
      .map((component) => component.selectedArtifact)
      .filter((artifact): artifact is ArtifactVM => !!artifact);
    const selectedBundles = this.getBundleOverridesForSave();

    this.saving = true;
    this.saveError = '';
    this.persistOverrides(targetId, this.buildSoftwareOverrides())
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ overrides, softwareSelectionResolutions }) => {
          this.saving = false;
          this.overridesSaved.emit({
            artifactOverrides:
              overrides?.artifactOverrides ?? selectedArtifacts,
            bundleOverrides: overrides?.bundleOverrides ?? selectedBundles,
            unresolvedOverrideIds: overrides?.unresolvedOverrideIds ?? [],
            softwareSelectionResolutions:
              softwareSelectionResolutions ?? this.softwareSelectionResolutions,
          });
        },
        error: (error: unknown) => {
          this.saving = false;
          this.saveError = this.getErrorMessage(
            error,
            this.translate.instant('software_override.errors.save'),
          );
        },
      });
  }

  private runDryRun(
    phase: 'bundles' | 'artifacts',
    onSuccess: () => void,
  ): void {
    const targetId = this.targetId;
    if (
      !targetId ||
      this.hasIncompleteBundleSelection ||
      this.dryRunLoading ||
      this.saving
    ) {
      return;
    }

    if (phase === 'bundles') {
      this.bundleDryRunCompleted = false;
      this.artifactDryRunCompleted = false;
    } else {
      this.artifactDryRunCompleted = false;
    }
    this.dryRunLoading = true;
    this.dryRunError = '';
    this.previewOverrides(targetId, this.buildSoftwareOverrides())
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resolutions) => {
          this.dryRunLoading = false;
          if (!Array.isArray(resolutions)) {
            this.dryRunError = this.translate.instant(
              'software_override.errors.dry_run_empty',
            );
            return;
          }

          this.softwareSelectionResolutions = resolutions;
          this.bundleDryRunCompleted = true;
          if (phase === 'artifacts') {
            this.artifactDryRunCompleted = true;
          }
          this.changeDetectorRef.detectChanges();
          onSuccess();
        },
        error: (error: unknown) => {
          this.dryRunLoading = false;
          this.dryRunError = this.getErrorMessage(
            error,
            this.translate.instant('software_override.errors.validation'),
          );
        },
      });
  }

  private buildSoftwareOverrides(): SoftwareVersionOverridesRequest {
    const artifactOverrides = this.softwareComponents
      .map((component) => component.selectedArtifact)
      .filter((artifact): artifact is ArtifactVM => !!artifact)
      .map((artifact) => this.toArtifactOverrideRequest(artifact));
    const bundleOverrides: BundleOverrideRequest[] =
      this.getBundleOverridesForSave().map((bundle) => ({ id: bundle.id }));

    return { artifactOverrides, bundleOverrides };
  }

  private previewOverrides(
    targetId: string,
    overrides: SoftwareVersionOverridesRequest,
  ): Observable<SoftwareSelectionResolutionDto[] | undefined> {
    if (this.licenseBatch) {
      return this.licenseBatchService
        .update(targetId, toLicenseBatchRequest(this.licenseBatch, overrides), {
          dryRun: true,
          projection: 'FULL',
        })
        .pipe(map((batch) => batch.licenseRedemptionInfo?.components));
    }
    return this.deviceService
      .previewDeviceUpdate(targetId, { softwareVersionOverrides: overrides })
      .pipe(map((device) => device.licenseRedemptionInfo?.components));
  }

  private persistOverrides(
    targetId: string,
    overrides: SoftwareVersionOverridesRequest,
  ): Observable<PersistedOverrides> {
    if (this.licenseBatch) {
      return this.licenseBatchService
        .update(targetId, toLicenseBatchRequest(this.licenseBatch, overrides), {
          projection: 'FULL',
        })
        .pipe(
          map((batch) => ({
            overrides: batch.softwareVersionOverrides,
            softwareSelectionResolutions:
              batch.licenseRedemptionInfo?.components,
          })),
        );
    }
    return this.deviceService
      .updateDevice(targetId, { softwareVersionOverrides: overrides })
      .pipe(
        map((device) => ({
          overrides: device.softwareVersionOverrides,
          softwareSelectionResolutions:
            device.licenseRedemptionInfo?.components,
        })),
      );
  }

  private invalidateBundleDryRun(): void {
    this.bundleDryRunCompleted = false;
    this.invalidateArtifactDryRun();
    this.softwareSelectionResolutions = [];
  }

  private invalidateArtifactDryRun(): void {
    this.artifactDryRunCompleted = false;
    this.dryRunError = '';
  }

  private initializeArtifactOverrides(): void {
    const licensedComponents = this.getSoftwareComponents();
    this.supportedProjects = licensedComponents;
    this.supportedProjectIds = [
      ...new Set(licensedComponents.map((component) => component.id)),
    ];
    const componentsById = new Map(
      licensedComponents.map((component) => [component.id, component]),
    );

    for (const artifact of this.initialArtifactOverrides) {
      if (!componentsById.has(artifact.projectId)) {
        componentsById.set(artifact.projectId, {
          id: artifact.projectId,
          componentName:
            artifact.projectName ||
            this.translate.instant(
              'software_override.artifact.software_project',
            ),
          description: this.translate.instant(
            'software_override.artifact.existing_override',
          ),
          kind: 'SPADOK_PROJECT',
        });
      }
    }

    this.softwareComponents = [...componentsById.values()]
      .sort((first, second) =>
        (first.componentName ?? '').localeCompare(second.componentName ?? ''),
      )
      .map((component) => {
        const initialOverride = this.initialArtifactOverrides.find(
          (artifact) => artifact.projectId === component.id,
        );
        const selectedArtifact = initialOverride
          ? this.toArtifactVM(initialOverride)
          : undefined;

        return {
          ...component,
          artifacts: selectedArtifact ? [selectedArtifact] : [],
          selectedArtifact,
        };
      });
  }

  private loadReleases(): void {
    this.releasesLoading = true;
    this.releasesLoadError = '';
    this.licenseBatchService
      .getReleases()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (releases) => {
          this.releaseOptions = releases.map((release) => {
            const existingOverride = this.initialBundleOverrides.find(
              (bundle) => this.getBundleReleaseId(bundle) === release.id,
            );
            return {
              release,
              selected: !!existingOverride,
              bundles: [],
              compatibleBundles: existingOverride ? [existingOverride] : [],
              selectedBundle: existingOverride,
              bundlesLoaded: false,
              bundlesLoading: false,
            };
          });
          this.releasesLoading = false;
        },
        error: (error: unknown) => {
          this.releasesLoading = false;
          this.releasesLoadError = this.getErrorMessage(
            error,
            this.translate.instant('software_override.errors.releases_load'),
          );
        },
      });
  }

  private getBundleOverridesForSave(): ReleaseBundleDto[] {
    const bundleIdsFromModifiedReleases = new Set(
      this.releaseOptions
        .filter((option) => this.modifiedReleaseIds.has(option.release.id))
        .flatMap((option) => option.bundles.map((bundle) => bundle.id)),
    );
    const preservedInitialBundles = this.initialBundleOverrides.filter(
      (bundle) => {
        const releaseId = this.getBundleReleaseId(bundle);
        return (
          (!releaseId || !this.modifiedReleaseIds.has(releaseId)) &&
          !bundleIdsFromModifiedReleases.has(bundle.id) &&
          !this.removedBundleIds.has(bundle.id)
        );
      },
    );
    const selectedBundles = this.releaseOptions
      .map((option) => option.selectedBundle)
      .filter((bundle): bundle is ReleaseBundleDto => !!bundle);
    const bundlesById = new Map<string, ReleaseBundleDto>();
    [...preservedInitialBundles, ...selectedBundles].forEach((bundle) =>
      bundlesById.set(bundle.id, bundle),
    );
    return [...bundlesById.values()];
  }

  private containsSupportedProject(
    bundle: ReleaseBundleDto,
    supportedProjectIds: ReadonlySet<string>,
  ): boolean {
    if (supportedProjectIds.size === 0) {
      return true;
    }

    return (bundle.artifacts ?? []).some((artifact) =>
      supportedProjectIds.has(artifact.projectId),
    );
  }

  private getBundleReleaseId(bundle: ReleaseBundleDto): string | undefined {
    return bundle.releaseId;
  }

  private getSoftwareComponents(): SoftwareOverrideComponent[] {
    const componentsById = new Map<string, LicensedComponentDto>();
    for (const batch of this.linkedLicenseBatches) {
      for (const component of batch.product?.licensedComponents ?? []) {
        if (component.kind === 'SPADOK_PROJECT') {
          componentsById.set(component.id, component);
        }
      }
    }

    return [...componentsById.values()]
      .map((component) => ({ ...component }))
      .sort((first, second) =>
        (first.componentName ?? '').localeCompare(second.componentName ?? ''),
      );
  }

  private toArtifactOverrideRequest(
    artifact: ArtifactVM,
  ): ArtifactOverrideRequest {
    return {
      id: artifact.id,
      version: artifact.version,
      description: artifact.description,
      projectId: artifact.projectId,
      projectName: artifact.projectName,
      blobPath: artifact.blobPath,
      fileName: artifact.fileName,
      uploadStatus: artifact.uploadStatus,
      scopes: artifact.scopes,
      compatibleHardwareComponents: artifact.compatibleHardwareComponents,
      artifactMaturity: artifact.artifactMaturity,
      releaseStatus: artifact.releaseStatus,
      createdAt: artifact.createdAt,
      updatedAt: artifact.updatedAt,
      downloadUrl: artifact.downloadUrl,
      createdBy: artifact.createdBy,
      updatedBy: artifact.updatedBy,
    };
  }

  private toArtifactVM(artifact: ArtifactResponse): ArtifactVM {
    const scopes = artifact.scopes ?? [];
    return {
      ...artifact,
      scopes,
      // An artifact gone from Spadok comes without a version
      versionAndScopes: scopes.length
        ? `${artifact.version} (${scopes.join(', ')})`
        : (artifact.version ?? artifact.id),
    };
  }

  private getErrorMessage(error: unknown, fallbackMessage: string): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      const typedError = error as {
        error?: { message?: string; detail?: string; title?: string };
        message?: string;
      };
      return (
        typedError.error?.message?.trim() ||
        typedError.error?.detail?.trim() ||
        typedError.error?.title?.trim() ||
        typedError.message?.trim() ||
        fallbackMessage
      );
    }

    return fallbackMessage;
  }
}
